import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, serverTimestamp, addDoc } from "firebase/firestore";

// Get user's role from Firestore
export async function getUserRole(db, email) {
  try {
    const docRef = doc(db, "userRoles", email);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().role;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user role:", error);
    return null;
  }
}

// Get full user role document
export async function getUserRoleDoc(db, email) {
  try {
    const docRef = doc(db, "userRoles", email);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    console.error("Error fetching user role doc:", error);
    return null;
  }
}

// Create or update user role
export async function setUserRole(db, email, role, name) {
  try {
    await setDoc(doc(db, "userRoles", email), {
      email,
      role,
      name,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error("Error setting user role:", error);
    throw error; // surface to caller so approvals don't silently succeed
  }
}

// ── Teacher Access Requests ──
// Request teacher access
export async function requestTeacherAccess(db, email, name, displayName, photoURL, department = "Physics") {
  try {
    // Check if already requested or approved
    const existing = await getDoc(doc(db, "teacherAccessRequests", email));
    if (existing.exists()) {
      throw new Error("You have already submitted a teacher access request.");
    }

    await setDoc(doc(db, "teacherAccessRequests", email), {
      email,
      name: name || displayName,
      photoURL: photoURL || "",
      department,
      status: "pending",
      requestedAt: serverTimestamp(),
      assignedClasses: [],
    });
    return true;
  } catch (error) {
    console.error("Error requesting teacher access:", error);
    throw error;
  }
}

// Get all teacher access requests (admin only)
export async function getTeacherAccessRequests(db, statusFilter = null) {
  try {
    let q;
    if (statusFilter) {
      q = query(collection(db, "teacherAccessRequests"), where("status", "==", statusFilter));
    } else {
      q = collection(db, "teacherAccessRequests");
    }
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => {
      if (a.status === "pending" && b.status !== "pending") return -1;
      if (b.status === "pending" && a.status !== "pending") return 1;
      return (b.requestedAt?.toMillis?.() || 0) - (a.requestedAt?.toMillis?.() || 0);
    });
  } catch (error) {
    console.error("Error fetching teacher requests:", error);
    return [];
  }
}

// Approve teacher access request
export async function approveTeacherRequest(db, email, assignedClasses, adminEmail) {
  try {
    const request = await getDoc(doc(db, "teacherAccessRequests", email));
    if (!request.exists()) throw new Error("Request not found");

    const facultyName = request.data().name;

    // Update request status
    await updateDoc(doc(db, "teacherAccessRequests", email), {
      status: "approved",
      assignedClasses: assignedClasses || [],
      approvedAt: serverTimestamp(),
      approvedBy: adminEmail,
    });

    // Create user role as teacher
    await setUserRole(db, email, "teacher", facultyName);

    return true;
  } catch (error) {
    console.error("Error approving teacher request:", error);
    throw error;
  }
}

// Reject teacher access request
export async function rejectTeacherRequest(db, email, adminEmail, reason = "") {
  try {
    await updateDoc(doc(db, "teacherAccessRequests", email), {
      status: "rejected",
      rejectedAt: serverTimestamp(),
      rejectedBy: adminEmail,
      rejectionReason: reason,
    });
    return true;
  } catch (error) {
    console.error("Error rejecting teacher request:", error);
    throw error;
  }
}

// Get teacher's assigned classes
export async function getTeacherAssignedClasses(db, email) {
  try {
    const request = await getDoc(doc(db, "teacherAccessRequests", email));
    if (request.exists() && request.data().status === "approved") {
      return request.data().assignedClasses || [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching teacher classes:", error);
    return [];
  }
}

// Check if user has pending teacher request
export async function getPendingTeacherRequest(db, email) {
  try {
    const docRef = doc(db, "teacherAccessRequests", email);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  } catch (error) {
    console.error("Error fetching pending request:", error);
    return null;
  }
}

// Utility functions
export function isAdmin(userRole) {
  return userRole === "admin";
}

export function isTeacher(userRole) {
  return userRole === "teacher";
}

export function isStudent(userRole) {
  return userRole === "student" || userRole === null;
}

export function canManageQuizzesInClass(userRole, assignedClasses, targetClass) {
  if (isAdmin(userRole)) return true;
  if (isTeacher(userRole) && assignedClasses && assignedClasses.includes(targetClass)) return true;
  return false;
}
